/**
 * SRT Parser Utility
 * Parses standard SRT subtitle files and strips stage directions
 */

export interface SubtitleCue {
  index: number;
  startTime: number; // seconds
  endTime: number;   // seconds
  text: string;
}

/**
 * Parse SRT timestamp to seconds
 * Format: HH:MM:SS,mmm (e.g., "00:00:01,918")
 */
function parseTimestamp(timestamp: string): number {
  const [time, millisStr] = timestamp.trim().split(',');
  const [hours, minutes, seconds] = time.split(':').map(Number);
  const millis = parseInt(millisStr || '0', 10);
  
  return hours * 3600 + minutes * 60 + seconds + millis / 1000;
}

/**
 * Strip stage directions from subtitle text
 * Removes content within square brackets like [chuckle], [grins], etc.
 */
function stripStageDirections(text: string): string {
  return text
    .replace(/\[.*?\]/g, '') // Remove bracketed content
    .replace(/\s+/g, ' ')     // Collapse multiple spaces
    .trim();
}

/**
 * Parse SRT content string into an array of SubtitleCues
 */
export function parseSRT(srtContent: string): SubtitleCue[] {
  const cues: SubtitleCue[] = [];
  
  // Normalize line endings and split into blocks
  const normalized = srtContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const blocks = normalized.split(/\n\n+/).filter(block => block.trim());
  
  for (const block of blocks) {
    const lines = block.split('\n').filter(line => line.trim());
    
    if (lines.length < 3) continue;
    
    // First line is the index
    const index = parseInt(lines[0], 10);
    if (isNaN(index)) continue;
    
    // Second line is the timestamp range
    const timestampLine = lines[1];
    const timestampMatch = timestampLine.match(/(\d{2}:\d{2}:\d{2}[,.:]\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2}[,.:]\d{3})/);
    
    if (!timestampMatch) continue;
    
    const startTime = parseTimestamp(timestampMatch[1].replace('.', ','));
    const endTime = parseTimestamp(timestampMatch[2].replace('.', ','));
    
    // Remaining lines are the subtitle text (join with space, not newline)
    const rawText = lines.slice(2).join(' ');
    const text = stripStageDirections(rawText);
    
    // Only add if there's actual text after stripping
    if (text) {
      cues.push({
        index: cues.length + 1,
        startTime,
        endTime,
        text
      });
    }
  }
  
  return cues;
}

/**
 * Find the active cue for a given playback time
 */
export function findActiveCue(cues: SubtitleCue[], currentTime: number): SubtitleCue | null {
  for (const cue of cues) {
    if (currentTime >= cue.startTime && currentTime <= cue.endTime) {
      return cue;
    }
  }
  return null;
}
